<template>
  <div
    class="selection-card"
    data-test-id="item-list-card"
    @click="displayModal=true"
  >
    <div class="card-content">
      <span class="card-label">{{ t(title) }}</span>
      <span class="card-action">{{ t(buttonLabel, { number: selectedCount }) }}</span>
    </div>
    <div class="card-badge" :class="{ 'has-items': selectedCount > 0 }">
      <span class="badge-count">{{ selectedCount }}</span>
    </div>
    <i class="fa-solid fa-chevron-right card-arrow" />
  </div>
  <ChooseItemsModal
    :key="name"
    :display-dialog="displayModal"
    :header="title"
    :name="name"
    :fields="fields"
    :items="items"
    :default-filters="defaultFilters"
    :enable-variants="enableVariants"
    @on-close-dialog="displayModal=false"
    @on-choose-items="onChooseItems"
  />
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType, ref, SetupContext,
} from 'vue';
import { useI18n } from 'vue-i18n';
import ChooseItemsModal from '@/components/modals/ChooseItemsModal.vue';
import {
  SpmTableFilter, StringMap,
} from '@/types';

export default defineComponent({
  name: 'BaseItemList',

  components: {
    ChooseItemsModal,
  },

  props: {
    modelValue: {
      type: Array as PropType<StringMap[]>,
      required: true,
    },

    title: {
      type: String,
      required: true,
    },

    buttonLabel: {
      type: String,
      required: true,
    },

    name: {
      type: String,
      required: true,
    },

    fields: {
      type: Object as PropType<StringMap>,
      required: true,
    },

    items: {
      type: String,
      required: false,
      default: '',
    },

    format: {
      type: Function,
      required: false,
      default: (value: StringMap) => value,
    },

    defaultFilters: {
      type: Array as PropType<SpmTableFilter[]>,
      required: false,
      default: () => [],
    },

    enableVariants: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['update:modelValue'],

  setup(props: { format: Function; items: string }, { emit }: SetupContext) {
    const { t } = useI18n();
    const displayModal = ref(false);
    const onChooseItems = (items: StringMap[]) => emit('update:modelValue', [...items.map((item: StringMap) => props.format(item))]);
    const selectedCount = computed(() => {
      if (!props.items || props.items.trim() === '') return 0;
      return props.items
        .split(',')
        .map((token) => token.trim())
        .filter((token) => token !== '')
        .reduce((count, token) => {
          const bracketMatch = token.match(/\[([0-9;]+)\]$/);
          if (bracketMatch) {
            return count + bracketMatch[1].split(';').filter((id) => id !== '').length;
          }
          return count + 1;
        }, 0);
    });

    return {
      t,
      displayModal,
      onChooseItems,
      selectedCount,
    };
  },

});
</script>

<style lang="scss" scoped>
@import '@/assets/scss/utilities/_variables.scss';

.selection-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: $white;
  border: 1px solid $solitude;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: $brand-color-primary;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);

    .card-arrow {
      color: $brand-color-primary;
      transform: translateX(2px);
    }
  }

  .card-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    overflow: hidden;

    .card-label {
      font-size: 0.7rem;
      color: $mid-grey;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    .card-action {
      font-size: 0.85rem;
      color: $tuna;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .card-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 26px;
    height: 26px;
    padding: 0 0.4rem;
    background: $solitude;
    border-radius: 13px;
    flex-shrink: 0;

    .badge-count {
      font-size: 0.8rem;
      font-weight: 600;
      color: $mid-grey;
    }

    &.has-items {
      background: $brand-color-primary;

      .badge-count {
        color: $white;
      }
    }
  }

  .card-arrow {
    color: $mid-grey;
    font-size: 0.75rem;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }
}
</style>
